import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { IShopPurchaseOffer } from "contexts/WebStoreProvider/types";
import { useAppDispatch } from "hooks";
import { WsDialogs } from "interfaces";
import { useLocation } from "react-router-dom";
import { offersApi } from "redux/apis";
import {
	getLimitedDeals,
	onDisableButtonsStickyDIP,
	onEnableButtonsStickyDIP
} from "redux/slices";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import usePopUpsV2 from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { getTimeLeft } from "utils/time";

interface DIPRewards {
	/**
	 *  @function onStickyAction
	 * 	@interface (offerId:string,rewardId:string,price:number) => void
	 * 	@description Receive stickyDIP data and open xsolla purchase dialog
	 */
	onStickyAction: (offerId: string, rewardId: string, price: number) => void;
}

export const useStickyDIPAction = (): DIPRewards => {
	const location = useLocation();
	const dispatch = useAppDispatch();
	const { open } = usePopUpsV2();
	const { openDialog } = useDialogModalRedux();
	const { authToken, nowTime } = useWebStoreContext();
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { wsStoreBuy, suppressOrderConfirmation, postUserMetadata } =
		useWebStoreContext();
	const { data: purchaseOffers } = offersApi.endpoints.getOffers.useQueryState({
		offerType: "singleItem",
		isAuthenticated: !!authToken?.value
	});

	const onStickyAction = (
		offerId: string,
		rewardId: string,
		price: number
	): void => {
		const buttonOffer: IShopPurchaseOffer | undefined =
			purchaseOffers?.offers.find((c: IShopPurchaseOffer) => c.id === offerId);
		if (buttonOffer?.upsellOfferConfig) {
			open({
				variant: `bundle-up::${offerId}`,
				id: `bundle-up::${offerId}`,
				replaceIfExists: true
			});
			return;
		}
		const buyProps = {
			offerId,
			rewardMultiplier: buttonOffer?.rewardMultiplier
		};
		wsStoreBuy(buyProps).then((invoice) => {
			if (invoice) {
				dispatch(onDisableButtonsStickyDIP());
				const timestampMs = new Date().getTime();
				openDialog(WsDialogs.XSOLLA_PURCHASE, {
					xsollaToken: invoice.xsollaToken,
					xsollaHost: invoice.xsollaHost,
					invoice,
					rewardMultiplier: buyProps.rewardMultiplier,
					suppressOrderConfirmation,
					source: "dips",
					pricePoint: price,
					postUserMetadata,
					onPurchaseStarted: (data) => {
						const minutesLeft = getTimeLeft(nowTime, buttonOffer?.dip?.endTime);
						reportEvent("purchase_started", {
							section_name: "limited_deals",
							offer_id: offerId,
							price_in_cents: price * 100,
							reward_id: rewardId,
							invoice_id: invoice?.id,
							paystation_id: data?.payStation
						});
						reportPlaygamiEvent("tp.web_ga.purchase_flow", {
							event_name: "purchase_started",
							event_type: "web_paystation_purchase",
							action: "purchase_started",
							section_name: "limited_deals",
							purchase_flow_id: data?.payStation,
							session_engaged: authToken?.value ? "1" : "0",
							price_in_cents: Math.round(invoice.price * 100),
							page_location: window.location.href,
							engagement_time_msec: (
								Number(data?.data?.milliseconds) - timestampMs
							).toString(),
							reward_id: invoice?.rewardId,
							offer_id: invoice?.offerId,
							ignore_referrer:
								process.env.REACT_APP_WITH_DEBUG === "true" ? "true" : "false",
							minutes_left: minutesLeft,
							page_title: location.pathname.replace("/", ""),
							page_referrer: invoice?.invoiceMetadata?.referer,
							priority: buttonOffer?.dip?.priority,
							invoice_id: invoice?.id
						});
					},
					onPurchaseSuccess: (data) => {
						const minutesLeft = getTimeLeft(nowTime, buttonOffer?.dip?.endTime);
						reportEvent("purchase_success", {
							invoice_id: invoice?.id
						});
						reportPlaygamiEvent("tp.web_ga.purchase_flow", {
							event_name: "purchase_success",
							event_type: "web_paystation_purchase",
							action: "purchase_success",
							section_name: "limited_deals",
							purchase_flow_id: data?.version,
							session_engaged: authToken?.value ? "1" : "0",
							price_in_cents: Math.round(invoice.price * 100),
							page_location: window.location.href,
							engagement_time_msec: (
								Number(data?.milliseconds) - timestampMs
							).toString(),
							reward_id: invoice?.rewardId,
							offer_id: invoice?.offerId,
							ignore_referrer:
								process.env.REACT_APP_WITH_DEBUG === "true" ? "true" : "false",
							minutes_left: minutesLeft,
							page_title: location.pathname.replace("/", ""),
							page_referrer: invoice?.invoiceMetadata?.referer,
							priority: buttonOffer?.dip?.priority,
							invoice_id: invoice?.id,
							transaction_id: data?.purchase_invoice_id?.toString()
						});
						dispatch(getLimitedDeals());
					},
					cbClose: () => {
						dispatch(onEnableButtonsStickyDIP());
						reportEvent("dialog_x_closed", {
							source: "xsolla_dialog",
							page_location: location.pathname
						});
					}
				});
			}
		});
	};

	return {
		onStickyAction
	};
};
